/**
 * Brand onboarding — the Cibles the user asks for on « Votre marque », held in memory until the
 * brand exists (every targets route needs a brandId behind BrandOwnershipGuard), then written by
 * `runPendingTargets` right after creation. Owner-approved 2026-11-03.
 *
 * Only ONE bulk import (sitemap or CSV) per onboarding: the targets manifest holds one
 * `importJob` at a time, so a second staged import would collide with the first. Manual rows
 * go through `POST /targets` and have no such lock. Personnes and Établissements are CSV-only
 * here: their manual forms (citation forms, Google Places) live in Cibles.
 */
import type { TargetRow } from '../targets/CATEGORIES';
import { TARGET_LIMITS } from '@traaker/shared';
import { api } from '../../services/api';
import { normalizeDomainInput, normalizeProductUrl } from '../targets/csv';
import { importCatalogueCsv, importCategoryCsv, importSitemapForDomain } from '../targets/importers';

export type QueueCategory = 'produit' | 'collection' | 'contenu' | 'etablissement' | 'personne' | 'marque';
export type QueueMethod = 'manual' | 'sitemap' | 'csv';
export type ManualCategory = 'produit' | 'collection' | 'contenu' | 'marque';

/** The add methods offered per category in onboarding (order = display order). */
export const METHODS_BY_CATEGORY: Record<QueueCategory, QueueMethod[]> = {
  produit: ['manual', 'sitemap', 'csv'],
  collection: ['manual', 'sitemap', 'csv'],
  contenu: ['manual', 'csv'],
  etablissement: ['csv'],
  personne: ['csv'],
  marque: ['manual', 'csv'],
};

export interface ManualRow {
  /** produit / collection / marque: the name; contenu: unused. */
  name: string;
  /** produit / collection / contenu: the page URL (the natural key); marque: its domain. */
  url: string;
}

export type PendingTarget =
  | { kind: 'manual'; category: ManualCategory; rows: ManualRow[] }
  /** The sitemap harvest classifies each URL as produit or collection itself. */
  | { kind: 'sitemap'; domain: string }
  | { kind: 'csv'; category: QueueCategory; file: File };

export const isBulk = (p: PendingTarget) => p.kind !== 'manual';

/** A second bulk import is refused while one is queued (one importJob at a time). */
export const canQueueBulk = (queue: PendingTarget[]) => !queue.some(isBulk);

/** Survives the create → `/onboarding/brand/:id` navigation (the wizard remounts). */
export const pendingTargetsCache = new Map<string, PendingTarget[]>();

/** The valid rows of a manual entry, as `POST /targets` targets. An invalid row is skipped. */
export function manualTargets(p: Extract<PendingTarget, { kind: 'manual' }>) {
  const out: Array<{ ownerId: 'own'; category: ManualCategory; label: string; fields: Array<{ name: string; value: string }> }> = [];
  for (const row of p.rows) {
    const name = row.name.trim();
    if (p.category === 'marque') {
      if (!name) continue;
      const fields = [{ name: 'name', value: name }];
      const domain = normalizeDomainInput(row.url);
      if (domain.valid) fields.push({ name: 'domain', value: domain.value });
      out.push({ ownerId: 'own', category: 'marque', label: name, fields });
      continue;
    }
    // produit / collection / contenu are identified by their URL (target_ids.derived_key).
    const url = p.category === 'contenu' ? normalizeDomainInput(row.url) : normalizeProductUrl(row.url);
    if (!url.valid) continue;
    const fields = [{ name: 'url', value: url.value }];
    if (name && p.category !== 'contenu') fields.push({ name: 'name', value: name });
    out.push({ ownerId: 'own', category: p.category, label: name || url.value, fields });
  }
  return out;
}

export interface PendingResult {
  target: PendingTarget;
  ok: boolean;
  error?: string;
}

/**
 * Write the queue for a just-created brand: manual rows first (fast, no lock), then the one
 * bulk import. Never throws — each failure is returned so the caller can show it.
 */
export async function runPendingTargets(brandId: string, queue: PendingTarget[]): Promise<PendingResult[]> {
  const results: PendingResult[] = [];
  const ordered = [...queue.filter((p) => !isBulk(p)), ...queue.filter(isBulk)];
  // The root marque is the only parent candidate a new brand has; CSV `marque_label` resolves against it.
  let marques: TargetRow[] | null = null;
  const loadMarques = async () => {
    if (marques) return marques;
    try {
      const res = await api.get<{ items: TargetRow[] }>(`/brands/${brandId}/targets?category=marque&limit=100`);
      marques = res.items ?? [];
    } catch {
      marques = [];
    }
    return marques;
  };
  for (const target of ordered) {
    try {
      if (target.kind === 'manual') {
        const targets = manualTargets(target);
        for (let i = 0; i < targets.length; i += TARGET_LIMITS.maxBatchRows) {
          await api.post(`/brands/${brandId}/targets`, { targets: targets.slice(i, i + TARGET_LIMITS.maxBatchRows) });
        }
      } else if (target.kind === 'sitemap') {
        await importSitemapForDomain(brandId, target.domain);
      } else if (target.category === 'produit' || target.category === 'collection') {
        await importCatalogueCsv(brandId, target.file, await loadMarques());
      } else {
        await importCategoryCsv(brandId, target.file, target.category, await loadMarques());
      }
      results.push({ target, ok: true });
    } catch (e) {
      results.push({ target, ok: false, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return results;
}
